import type { RoundEvent } from "@slot/client-engine";
import type { CascadeMechanic } from "./CascadeMechanic";
import type { PreparedRevealPlan } from "./GridReveal";
import type { CascadePhaseMotionTuning } from "./CascadeMotionTuning";
import type { RemovalTuning } from "./CascadeWinTuning";
import type { GridCodec, Log, PreparedDropPlan, PresentationBarrier } from "@slot/presentation";
import type { BaseGridPresenter } from "@slot/mechanics-core";

/**
 * Optional lifecycle hooks a game wires to inject behaviour at well-defined cascade moments. Unwired,
 * the pipeline behaves exactly as if the surface did not exist.
 *
 * - `onAfterRemove` fires after the per-cell shrink completes inside `removeSymbolsByMask`
 *   (removal-contingent VFX, stingers, analytics).
 * - `onBeforeReveal` fires before the reveal animates in `presentEventFrame` (HUD prep, queued
 *   audio, background transitions).
 *
 * A hook may be sync or async; the pipeline awaits either, so an async hook delays the next step.
 */
export interface CascadeGridPresenterHooks {
  onAfterRemove?: (positionMask: string) => Promise<void> | void;
  onBeforeReveal?: (event: RoundEvent) => Promise<void> | void;
}

/**
 * The cascade mechanic's adapter to the client-engine event frames: boot / recovery frames print
 * instantly; a reveal-like frame is decoded and spawned above the grid on prepare (after the spin
 * barrier) and dropped in on present; a collapse-like frame is planned as a cascade against the last
 * removal mask on prepare and played on present.
 */
export class CascadeGridPresenter implements BaseGridPresenter {
  private readonly mechanic: CascadeMechanic;
  private readonly gridCodec: GridCodec;
  private readonly log: Log;
  private readonly presentationBarrier: PresentationBarrier;
  private readonly removalTuning: RemovalTuning;
  private readonly cascadeTuning: CascadePhaseMotionTuning;
  private readonly hooks: CascadeGridPresenterHooks | undefined;

  private lastRemovalMask: string | null = null;
  private lastPreparedEventKey: string | null = null;
  private pendingCascadePlan: PreparedDropPlan | null = null;
  private pendingRevealPlan: PreparedRevealPlan | null = null;

  public constructor(input: {
    mechanic: CascadeMechanic;
    gridCodec: GridCodec;
    presentationBarrier: PresentationBarrier;
    removalTuning: RemovalTuning;
    cascadeTuning: CascadePhaseMotionTuning;
    hooks?: CascadeGridPresenterHooks;
    log?: Log;
  }) {
    this.mechanic = input.mechanic;
    this.gridCodec = input.gridCodec;
    this.presentationBarrier = input.presentationBarrier;
    this.removalTuning = input.removalTuning;
    this.cascadeTuning = input.cascadeTuning;
    this.hooks = input.hooks;
    this.log = input.log ?? (() => {});
  }

  public async prepareEventFrame(event: RoundEvent): Promise<void> {
    const key = this.computeEventKey(event);
    if (this.lastPreparedEventKey === key) return;

    const encoded = event.eventContent.encodedGridSnapshot ?? "";
    if (encoded.length === 0) {
      this.lastPreparedEventKey = key;
      this.pendingCascadePlan = null;
      this.pendingRevealPlan = null;
      return;
    }

    const nextModel = this.gridCodec.decode(encoded);

    const collapseLike = this.isCollapseLike(event.eventTypeName);

    // A reveal must not touch the grid until the pre-spin drop-out has released the barrier.
    if (!collapseLike) {
      await this.presentationBarrier.wait("spin");
    }

    if (collapseLike && this.lastRemovalMask) {
      this.log(`[CascadeGridPresenter] prepare-cascade ${event.eventTypeName}`);
      this.pendingCascadePlan = this.mechanic.prepareCascadeToModel({
        removedPositionMask: this.lastRemovalMask,
        nextModel,
        fallDurationSeconds: this.cascadeTuning.fallDurationSeconds,
        spawnDurationSeconds: this.cascadeTuning.spawnDurationSeconds,
        columnStaggerSeconds: this.cascadeTuning.columnStaggerSeconds,
        withinColumnStaggerSeconds: this.cascadeTuning.withinColumnStaggerSeconds,
        strictSymbolConsistencyCheck: true,
      });
      this.pendingRevealPlan = null;
      this.lastPreparedEventKey = key;
      return;
    }

    this.pendingRevealPlan = this.mechanic.prepareRevealToModel({ nextModel, strictSymbolConsistencyCheck: true });
    this.pendingCascadePlan = null;
    this.lastPreparedEventKey = key;
    this.log(`[CascadeGridPresenter] prepare ${event.eventTypeName}`);
  }

  public async presentEventFrame(event: RoundEvent): Promise<void> {
    const key = this.computeEventKey(event);
    if (this.lastPreparedEventKey !== key) {
      await this.prepareEventFrame(event);
    }

    if (this.isCollapseLike(event.eventTypeName) && this.pendingCascadePlan) {
      this.log(`[CascadeGridPresenter] cascade ${event.eventTypeName}`);
      const plan = this.pendingCascadePlan;
      this.pendingCascadePlan = null;
      this.lastRemovalMask = null;
      await this.mechanic.animatePreparedCascade(plan);
      return;
    }

    if (this.pendingRevealPlan) {
      this.log(`[CascadeGridPresenter] reveal ${event.eventTypeName}`);
      const plan = this.pendingRevealPlan;
      this.pendingRevealPlan = null;
      await this.hooks?.onBeforeReveal?.(event);
      await this.mechanic.animatePreparedReveal(plan);
    } else {
      this.log(`[CascadeGridPresenter] frame ${event.eventTypeName}`);
    }
    this.lastRemovalMask = null;
  }

  /** Paints a grid without motion (boot, recovery), through the same decode path; no reveal or cascade is left pending. */
  public async paintGrid(encodedGridSnapshot: string): Promise<void> {
    this.log("[CascadeGridPresenter] paint");
    this.mechanic.renderModel(this.gridCodec.decode(encodedGridSnapshot));
    this.lastPreparedEventKey = null;
    this.pendingRevealPlan = null;
    this.pendingCascadePlan = null;
    this.lastRemovalMask = null;
  }

  public async removeSymbolsByMask(positionMask: string): Promise<void> {
    // Shrink only — the plugin's removal handler (`buildCascadeReplayPlugin`) sleeps `postRemovalHoldSeconds`
    // once the composite resolves, so the hold begins after BOTH the shrink and the win highlight's un-dim fade.
    this.lastRemovalMask = positionMask;
    await this.mechanic.removeByMask(positionMask, {
      shrinkSeconds: this.removalTuning.shrinkSeconds,
      columnStaggerSeconds: this.removalTuning.columnStaggerSeconds,
      withinColumnStaggerSeconds: this.removalTuning.withinColumnStaggerSeconds,
    });
    await this.hooks?.onAfterRemove?.(positionMask);
  }

  private isCollapseLike(name: string): boolean {
    return name === "collapse" || name.endsWith("_collapse") || name.includes("collapse");
  }

  private computeEventKey(event: RoundEvent): string {
    return `${String(event.roundIdentifier ?? "")}::${event.eventTypeName}::${String(event.eventSequenceNumber ?? "")}`;
  }
}
